import { WizardStep, WizardStepId } from '../types/wizard';
import { APPLICANT_BASE_PATH } from '../config';

// All wizard steps in order
export const WIZARD_STEPS: Array<{ id: WizardStepId; title: string; description: string; icon: string }> = [
  {
    id: 'submitted',
    title: 'Application Submitted',
    description: 'Your application has been received',
    icon: '✓'
  },
  {
    id: 'fit-assessment',
    title: 'Quick Questions',
    description: 'Quick 1-3 minute questionnaire',
    icon: '📋'
  },
  {
    id: 'local-knowledge',
    title: 'Local Knowledge Check',
    description: '10-question quiz about your area',
    icon: '🗺️'
  },
  {
    id: 'video-intro',
    title: 'Video Introduction',
    description: '20-30 second video (optional)',
    icon: '🎥'
  },
  {
    id: 'upload-documents',
    title: 'Upload Documents',
    description: "Submit licence and insurance",
    icon: '📄'
  },
  {
    id: 'document-review',
    title: 'Document Review',
    description: 'Waiting for document review',
    icon: '⏳'
  },
  {
    id: 'schedule-interview',
    title: 'Schedule Interview',
    description: 'Book your interview time',
    icon: '📅'
  },
  {
    id: 'interview-scheduled',
    title: 'Interview Scheduled',
    description: 'Interview confirmed',
    icon: '✓'
  },
  {
    id: 'interview-complete',
    title: 'Interview Complete',
    description: 'Interview finished',
    icon: '✓'
  },
  {
    id: 'background-check',
    title: 'Background Check',
    description: 'Final verification',
    icon: '🔍'
  },
  {
    id: 'approved',
    title: 'Approved',
    description: 'Ready to start delivering!',
    icon: '🎉'
  }
];

export const APPLICANT_V2_WIZARD_STEPS = WIZARD_STEPS.filter((step) => [
  'submitted',
  'fit-assessment',
  'local-knowledge',
  'video-intro',
  'upload-documents',
  'document-review',
  'approved',
].includes(step.id));

// Map DynamoDB ApplicantStep values to wizard step IDs
export function mapApplicantStepToWizardStep(applicantStep: string): WizardStepId {
  const mapping: Record<string, WizardStepId> = {
    'Applied': 'fit-assessment',
    'Fit Assessment Complete': 'local-knowledge',
    'Knowledge Check Complete': 'video-intro',
    'Video Complete': 'upload-documents',
    'Documents Approved': 'schedule-interview',
    'Documents Rejected': 'upload-documents', // Back to upload
    'Scheduling Interview': 'schedule-interview',
    'Interview Scheduled': 'interview-scheduled',
    'Scheduled Interview': 'interview-scheduled', // Alias
    'Interview Complete': 'upload-documents',
    'Interview Completed': 'upload-documents', // Alias (dashboard uses this)
    'Documents Submitted': 'document-review',
    'Background Check': 'background-check',
    'Approved': 'approved',
    'Rejected': 'approved', // Show on approved screen with rejection message
    'Completed': 'approved'
  };

  return mapping[applicantStep] || 'fit-assessment';
}

export function mapV2ApplicationStateToWizardStep(
  currentStep: string,
  applicationStatus: string
): WizardStepId {
  if (applicationStatus === 'APPROVED') return 'approved';
  if (applicationStatus === 'NOT_APPROVED') return 'approved';
  if (applicationStatus === 'SUBMITTED') return 'document-review';

  const mapping: Record<string, WizardStepId> = {
    FIT_ASSESSMENT: 'fit-assessment',
    LOCAL_KNOWLEDGE: 'local-knowledge',
    VIDEO: 'video-intro',
    REQUIREMENTS: 'upload-documents',
    COMPLETE: 'document-review',
  };

  return mapping[currentStep] || 'fit-assessment';
}

export function getV2WizardRedirect(
  pathname: string,
  currentStepId: WizardStepId
): string | null {
  return parseStepFromPath(pathname) === currentStepId
    ? null
    : `/wizard/${currentStepId}`;
}

// Determine step status based on current progress
export function calculateStepStatuses(
  currentStepId: WizardStepId,
  applicantStep: string,
  isApplicantV2 = false
): WizardStep[] {
  const configuredSteps = isApplicantV2 ? APPLICANT_V2_WIZARD_STEPS : WIZARD_STEPS;
  const currentIndex = configuredSteps.findIndex(step => step.id === currentStepId);

  return configuredSteps.map((step, index) => {
    if (index < currentIndex) {
      return {
        ...step,
        status: 'completed' as const
      };
    } else if (index === currentIndex) {
      return {
        ...step,
        status: 'current' as const
      };
    } else if (step.id === 'document-review' || step.id === 'interview-scheduled' || step.id === 'interview-complete' || step.id === 'background-check') {
      return {
        ...step,
        status: 'pending' as const
      };
    } else {
      return {
        ...step,
        status: 'locked' as const
      };
    }
  });
}

// Get next step in the sequence
export function getNextStep(currentStepId: WizardStepId): WizardStepId | null {
  const currentIndex = WIZARD_STEPS.findIndex(step => step.id === currentStepId);
  if (currentIndex === -1 || currentIndex === WIZARD_STEPS.length - 1) {
    return null;
  }
  return WIZARD_STEPS[currentIndex + 1].id;
}

// Get previous step in the sequence
export function getPreviousStep(currentStepId: WizardStepId): WizardStepId | null {
  const currentIndex = WIZARD_STEPS.findIndex(step => step.id === currentStepId);
  if (currentIndex <= 0) {
    return null;
  }
  return WIZARD_STEPS[currentIndex - 1].id;
}

// Check if user can navigate to a specific step
export function canNavigateToStep(targetStepId: WizardStepId, currentStepId: WizardStepId, steps: WizardStep[]): boolean {
  const targetStep = steps.find(s => s.id === targetStepId);
  if (!targetStep) return false;

  // Can navigate to completed steps (review)
  if (targetStep.status === 'completed') return true;

  // Can navigate to current step
  if (targetStep.status === 'current') return true;

  // Cannot navigate to locked or pending steps
  return false;
}

// Get step display name for URLs
export function getStepPath(stepId: WizardStepId): string {
  return stepId;
}

// Parse step from URL path
export function parseStepFromPath(path: string): WizardStepId | null {
  const withoutBasePath = APPLICANT_BASE_PATH && path.startsWith(`${APPLICANT_BASE_PATH}/`)
    ? path.slice(APPLICANT_BASE_PATH.length)
    : path;
  const marker = '/wizard/';
  const markerIndex = withoutBasePath.indexOf(marker);
  if (markerIndex === -1) return null;
  const stepId = withoutBasePath.slice(markerIndex + marker.length).split('/')[0];
  const validStepIds = WIZARD_STEPS.map(s => s.id);
  return validStepIds.includes(stepId as WizardStepId) ? stepId as WizardStepId : null;
}
